import { useState } from 'react';
import styled from '@emotion/styled';
import { PortableText } from '@portabletext/react';

import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { RichHeading } from '@/components/common/Heading';
import { PageTextContent } from '@/components/common/PageTextContent';
import { FormContainer } from '@/components/forms/FormContainer/FormContainer';
import { FormError, HubspotForm } from '@/components/forms/HubspotForm';
import { FormLayout } from '@/components/layout/FormLayout';
import { Stack } from '@/components/layout/Stack';
import { AbFormContainer, AbTestWrapper } from '@/lib/AbTesting';
import { BASE_REVALIDATE } from '@/lib/constants';
import { getHubspotForm } from '@/lib/HubspotFormApi';
import {
  formQuery,
  localeTranslationsQuery,
  navigationCollectionQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient } from '@/lib/sanityUtils';
import { getValidLocale } from '@/lib/utils';
import { bodyLarge } from '@/styles/typography';

import type {
  SanityAbTestFormType,
  SanityHubspotFormType,
} from '@/types/sanity';
import type {
  Navigation_exType,
  SiteSettings,
  Translation,
} from '@/types/shared';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';

const slug = 'employer-contact-form';

export const getStaticProps = (async ({ locale }) => {
  const client = getClient();

  const validLocale = getValidLocale(locale);

  if (!validLocale) {
    return {
      notFound: true,
    };
  }

  const contactForm = await client.fetch<SanityHubspotFormType>(
    formQuery({ slug, locale: validLocale }),
  );

  const siteSettings = await client.fetch<SiteSettings>(
    siteSettingsQuery({ locale: validLocale }),
  );

  const translations = await client.fetch<Translation[] | null>(
    localeTranslationsQuery(contactForm),
  );

  const navigationCollection = await client.fetch<Navigation_exType[]>(
    navigationCollectionQuery({ locale: validLocale }),
  );

  if (!contactForm) {
    return {
      notFound: true,
    };
  }

  const formData = await getHubspotForm(contactForm.hubspotFormId);

  return {
    props: {
      contactForm,
      formData,
      locale: validLocale,
      translations,
      siteSettings,
      navigationCollection,
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

const TestComponent = (props) => {
  const {
    simpleLayout,
    testimonialName,
    testimonialQuote,
    testimonialLogo,
    testimonialRole,
    pageTitle: { heading },
    body,
    caption,
    showFormHeader,
    formHeaderTitle,
    formHeaderDescription,
    DYId,
  } = props;

  if (!simpleLayout) {
    return (
      <OldPageLayout>
        <PageTextContent
          heading={heading}
          text={body}
          caption={caption}
          formHeaderTitle={formHeaderTitle ?? null}
          testimonialCard={{
            body: testimonialQuote,
            logo: testimonialLogo,
            author: testimonialName,
            authorRole: testimonialRole,
          }}
        />
        <FormContainer
          header={
            showFormHeader
              ? {
                  title: formHeaderTitle,
                  description: formHeaderDescription,
                }
              : null
          }
        >
          <AbFormContainer {...props} id={DYId} />
        </FormContainer>
      </OldPageLayout>
    );
  }
  return (
    <SimplePageLayout>
      <StyledStack>
        <RichHeading heading={heading} size="display-md" />
        {!!body && (
          <BulletPointsStyling>
            <BulletPoints paragraphFontSize="sm">
              <PortableText value={body} />
            </BulletPoints>
          </BulletPointsStyling>
        )}
      </StyledStack>
      <FormContainer>
        <AbFormContainer {...props} id={DYId} />
      </FormContainer>
    </SimplePageLayout>
  );
};

const ContactForm = ({
  contactForm,
  formData,
  translations,
  siteSettings,
  navigationCollection,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const [showFormHeader, setShowFormHeader] = useState(true);

  const handleOnSubmitForm = () => {
    setShowFormHeader(false);
  };

  const abTest = contactForm.abTest as unknown as SanityAbTestFormType;

  return (
    <FormLayout
      SEO={contactForm?.SEO}
      localeTranslations={translations}
      navigationCollection={navigationCollection}
      siteSettings={siteSettings}
      dynamicYield={{
        contextPageType: 'CATEGORY',
        contextDataType: 'CTA page',
      }}
    >
      {abTest?.variations ? (
        <AbTestWrapper
          abTest={abTest}
          component={TestComponent}
          onSubmit={handleOnSubmitForm}
          formType="contactPage"
          showFormHeader={showFormHeader}
          formHeaderTitle={contactForm?.formHeaderTitle}
          formHeaderDescription={contactForm?.formHeaderDescription}
        />
      ) : formData?.status === 'error' ? (
        <FormError />
      ) : (
        <HubspotForm
          formData={formData}
          breakpoints={contactForm.breakpoints}
          onSubmit={handleOnSubmitForm}
          formType="contactPage"
        />
      )}
    </FormLayout>
  );
};

export default ContactForm;

const OldPageLayout = styled.div`
  display: flex;
  margin-top: var(--space-page-sm);
  flex-direction: column;

  ${screen.md} {
    flex-direction: row;
  }
`;

const BulletPointsStyling = styled.div`
  p {
    ${bodyLarge}
  }
  ul {
    margin-top: var(--spacing-xx-small, 8px);
  }
`;
const SimplePageLayout = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--space-page-sm);
  width: 100%;
  background: unset;
  h1 {
    text-align: center;
  }

  .footer-container {
    background: unset;
  }
`;

const StyledStack = styled(Stack)`
  padding: 0 var(--section-gutter) var(--spacing-large);
`;
